// Import the actual config modules
import merge from 'lodash/merge';

// eslint-disable-next-line @typescript-eslint/ban-ts-comment
// @ts-ignore
import developmentConfig from '../development';
// eslint-disable-next-line @typescript-eslint/ban-ts-comment
// @ts-ignore
import localhostConfig from '../localhost';
// eslint-disable-next-line @typescript-eslint/ban-ts-comment
// @ts-ignore
import type config from '../production';
// eslint-disable-next-line @typescript-eslint/ban-ts-comment
// @ts-ignore
import productionConfig from '../production';
// eslint-disable-next-line @typescript-eslint/ban-ts-comment
// @ts-ignore
import stagingConfig from '../staging';

export type ConfigType = typeof config;

const PROD_DOMAINS = [
  'dash.newfront.com',
  'eks-dash.newfront.com',
  'app.newfront.com',
  'eks-app.newfront.com',
  'legacy.newfront.com',
];

const LOCALHOST_DOMAINS = ['dash.local.internal', 'app.local.internal'];

const STAGING_DOMAINS = [
  'dash.newfront-staging.com',
  'app.newfront-staging.com',
  'stage-legacy.newfront-development.com',
];

export function getEnvFromUrl(): 'stage' | 'prod' | 'dev' | 'localhost' | undefined {
  if (typeof window === 'undefined') {
    return;
  }

  const url = new URL(window.location.href);
  const urlParts = url.hostname.split('.');
  const subdomain = urlParts[0];
  const mainDomain = urlParts[1];

  if (subdomain.startsWith('dev')) {
    return 'dev';
  }

  if (mainDomain?.startsWith('ngrok') || LOCALHOST_DOMAINS.includes(url.hostname)) {
    return 'localhost';
  }

  if (subdomain.startsWith('stage') || STAGING_DOMAINS.includes(url.hostname)) {
    return 'stage';
  }

  if (subdomain.startsWith('prod') || PROD_DOMAINS.includes(url.hostname)) {
    return 'prod';
  }
}

export interface GetConfigOptions<TCustomConfig = Record<string, unknown>> {
  customConfigs?: {
    production?: TCustomConfig;
    staging?: TCustomConfig;
    localhost?: TCustomConfig;
    development?: TCustomConfig;
  };
  deepMerge?: boolean;
}

/**
 * Get configuration for the current environment.
 * Handles all environments (prod, stage, localhost, dev) with optional custom config merging.
 *
 * @param options - Optional configuration options
 * @param options.customConfigs - Custom configurations to merge for each environment
 * @param options.deepMerge - Whether to use deep merge (requires lodash/merge) or shallow merge
 * @returns The configuration object for the current environment
 */
export function getConfig<TCustomConfig = Record<string, unknown>>(
  options: GetConfigOptions<TCustomConfig> = {},
): ConfigType & TCustomConfig {
  /* eslint-disable no-process-env */
  const env = process.env.NEWFRONT_ENV || getEnvFromUrl();
  const { customConfigs, deepMerge = false } = options;

  let baseConfig: ConfigType;
  let customConfig: TCustomConfig | undefined;

  /**
   * We handle the config this way so that Webpack will tree-shake the unused config
   * from the final build on the client.
   */
  if (env === 'prod') {
    baseConfig = productionConfig;
    customConfig = customConfigs?.production;
  } else if (env === 'stage') {
    baseConfig = stagingConfig;
    customConfig = customConfigs?.staging;
  } else if (env === 'localhost') {
    baseConfig = localhostConfig;
    customConfig = customConfigs?.localhost;
  } else {
    baseConfig = developmentConfig;
    customConfig = customConfigs?.development;
  }

  // If no custom config, return base config
  if (!customConfig) {
    return baseConfig as ConfigType & TCustomConfig;
  }

  if (deepMerge) {
    try {
      return merge({}, baseConfig, customConfig);
    } catch (error) {
      return { ...baseConfig, ...customConfig };
    }
  }

  return { ...baseConfig, ...customConfig };
}
